import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Bot, Zap, Clock, Loader2, MessageSquare, PhoneForwarded, FileText, Activity, HelpCircle, BadgeCheck } from "lucide-react";
import { statisticService } from "@/services/statisticService";
import { useBilling } from "@/hooks/useBilling";
import { billingService, Plan } from "@/services/billingService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";

const Billing = () => {
  const [usedMinutes, setUsedMinutes] = useState(0);
  
  const { subscription, isLoading: isBillingLoading } = useBilling();

  // Handle URL params for success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success === 'true') {
      toast.success('Subscription updated successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      toast.info('Subscription update was canceled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const { data: availablePlans, isLoading: isPlansLoading } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: () => billingService.getPlans()
  });

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const usage = await statisticService.getVoiceUsage();
        setUsedMinutes(usage.totalMinutesCeil);
      } catch (error) {
        console.error("Failed to fetch usage minutes:", error);
      }
    };

    fetchUsage();
  }, []);

  const currentPlanName = subscription?.items?.data?.[0]?.price?.product?.name ?? null;

  const findStripePlanByName = (planName: string): Plan | null => {
    if (!availablePlans) return null;
    const normalized = planName.trim().toLowerCase();
    const exactMatch = availablePlans.find(plan => plan.name.trim().toLowerCase() === normalized);
    if (exactMatch) return exactMatch;
    return (
      availablePlans.find(plan => {
        const planLabel = plan.name.trim().toLowerCase();
        return planLabel.includes(normalized) || normalized.includes(planLabel);
      }) || null
    );
  };

  const formatPlanPrice = (plan: Plan): string => {
    const currency = plan.price.currency?.toUpperCase() || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0
    }).format(plan.price.amount / 100);
  };

  const formatPlanInterval = (plan: Plan): string => {
    if (!plan.price.interval) return "month";
    if (plan.price.intervalCount && plan.price.intervalCount > 1) {
      return `${plan.price.intervalCount} ${plan.price.interval}s`;
    }
    return plan.price.interval;
  };

  useEffect(() => {
    console.log('Billing component state:', {
      currentPlanName,
      usedMinutes,
      plans: displayPlans.map(p => p.name),
      availablePlans
    });
  }, [currentPlanName, usedMinutes, availablePlans]);

  const handlePlanSelect = async (planName: string) => {
    if (!availablePlans) return;

    const plan = findStripePlanByName(planName);

    if (!plan) {
      toast.error(`Plan "${planName}" is currently unavailable.`);
      return;
    }

    try {
      let isUpgrade = true;
      if (currentPlanName) {
        const currentPlan = findStripePlanByName(currentPlanName);
        if (currentPlan && plan.price.amount < currentPlan.price.amount) {
          isUpgrade = false;
        }
      }

      const response = await billingService.createCheckoutSession(plan.price.id, isUpgrade, "sophie");
      if (response && response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("Failed to initiate checkout:", error);
      toast.error("Failed to initiate checkout. Please try again.");
    }
  };

  const displayPlans = [
    {
      name: "Starter",
      price: "$179",
      period: "CAD/month",
      minutes: 250,
      usedMinutes: usedMinutes,
      features: [
        "250 minutes of AI calls",
        "5 simultaneous calls",
        "SLA 3 days",
      ],
    },
    {
      name: "Growth",
      price: "$249",
      period: "CAD/month",
      minutes: 400,
      usedMinutes: usedMinutes,
      popular: true,
      features: [
        "400 minutes of AI calls",
        "10 simultaneous calls",
        "SLA 1 days",
      ],
    },
    {
      name: "Business Pro",
      price: "$449",
      period: "CAD/month",
      minutes: 800,
      usedMinutes: usedMinutes,
      features: [
        "800 minutes of AI calls",
        "20 simultaneous calls",
        "Priority human support - SLA 3 hours"
      ],
    },
  ];

  const overageRateCadPerMinute = 1;

  const getUsagePercentage = (plan: typeof displayPlans[0]) => {
    return Math.min(100, (plan.usedMinutes / plan.minutes) * 100);
  };

  const getOverageMinutes = (plan: typeof displayPlans[0]) => {
    return Math.max(0, plan.usedMinutes - plan.minutes);
  };

  const allPlansInclude = [
    {
      icon: Zap,
      title: "Instant Setup",
      description: "Get up and running in minutes, not days"
    },
    {
      icon: Bot,
      title: "24/7 Availability",
      description: "Sophie AI never sleeps, never takes breaks"
    },
    {
      icon: HelpCircle,
      title: "Answers general business questions",
      description: "Handles common FAQs automatically"
    },
    {
      icon: PhoneForwarded,
      title: "Call redirect",
      description: "Route calls to the right person or team"
    },
    {
      icon: FileText,
      title: "Call transcript",
      description: "Full transcripts for every conversation"
    },
    {
      icon: Activity,
      title: "Sentiment reporting",
      description: "Track caller mood and trends over time"
    },
    {
      icon: MessageSquare,
      title: "SMS summaries",
      description: "Instant text summaries after each call"
    },
    {
      icon: BadgeCheck,
      title: "SMS confirmations",
      description: "Users get a confirmation SMS about the action performed"
    },
    {
      icon: Clock,
      title: "Cancel anytime",
      description: "No long-term contracts or penalties"
    }
  ];

  if (isBillingLoading || isPlansLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">Choose Your Plan</h1>
          <p className="text-muted-foreground">Select the perfect plan for your business needs</p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayPlans.map((plan) => {
            const isCurrent = currentPlanName
              ? currentPlanName.toLowerCase().includes(plan.name.toLowerCase()) ||
                plan.name.toLowerCase().includes(currentPlanName.toLowerCase())
              : false;

            const stripePlan = findStripePlanByName(plan.name);
            const displayPrice = stripePlan ? formatPlanPrice(stripePlan) : plan.price;
            const displayPeriod = stripePlan ? formatPlanInterval(stripePlan) : plan.period;

            return (
              <Card 
                key={plan.name} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${
                  isCurrent ? 'border-2 border-primary' : 'border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2">
                    <Badge className="rounded-t-none rounded-b-lg bg-primary px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}

                <div className="p-6 space-y-6">
                  {/* Plan Header */}
                  <div className={plan.popular ? "pt-6" : ""}>
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-foreground">{displayPrice}</span>
                      <span className="text-muted-foreground text-sm">/{displayPeriod}</span>
                    </div>
                  </div>

                  {/* Usage - Only on current plan */}
                  {isCurrent && (
                    <div className="space-y-2 p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Usage</span>
                        <span className="font-semibold text-foreground">
                          {usedMinutes} / {plan.minutes} min
                          {getOverageMinutes(plan) > 0 ? ` (+${getOverageMinutes(plan)} over)` : ""}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(plan)} 
                        className="h-2"
                      />
                      <p className="text-xs text-muted-foreground">
                        {getOverageMinutes(plan) > 0
                          ? `${getOverageMinutes(plan)} minutes over`
                          : `${Math.max(0, plan.minutes - usedMinutes)} minutes remaining`}
                      </p>
                    </div>
                  )}

                  {/* Features */}
                  <div className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-sm text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Action Button */}
                  <Button 
                    className="w-full" 
                    variant={isCurrent ? "outline" : plan.popular ? "default" : "outline"}
                    disabled={isCurrent}
                    onClick={() => handlePlanSelect(plan.name)}
                  >
                    {isCurrent ? "Current Plan" : "Select Plan"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className="p-6">
          <div className="flex flex-col gap-2 text-center">
            <h3 className="text-lg font-semibold">Overage Minutes</h3>
            <p className="text-sm text-muted-foreground">
              Additional minutes are billed at{" "}
              <span className="font-medium text-foreground">
                CAD ${overageRateCadPerMinute.toFixed(2)} per minute
              </span>
              .
            </p>
          </div>
        </Card>

        {/* All Plans Include Section */}
        <Card className="p-8">
          <h2 className="text-2xl font-bold mb-6 text-center">All Plans Include</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {allPlansInclude.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="flex flex-col items-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Billing;
